import { useState, useRef, useCallback, useEffect } from "react";

function setElementWidth(el, widthPx) {
  el.style.width = widthPx;
  el.style.minWidth = widthPx;
  el.style.maxWidth = widthPx;
}

function clearBodyDragStyles() {
  document.body.style.userSelect = '';
  document.body.style.cursor = '';
}

export const useResizeDataTableColumns = ({
  setColWidthConfig,
  colWidthConfig,
  theme,
  builderProps,
  minColWidth = 200,
}) => {

  const [hoveredDetector, setHoveredDetector] = useState(undefined);
  const [colResizeState, setColResizeState] = useState(undefined);
  const colResizeStateRef = useRef(undefined);
  const finalWidthRef = useRef(null);

  const colWidthConfigRef = useRef(colWidthConfig);
  useEffect(() => {
    colWidthConfigRef.current = colWidthConfig;
  }, [colWidthConfig]);

  useEffect(() => {
    return clearBodyDragStyles;
  }, []);

  const applyWidthToDOM = useCallback((colId, width) => {
    const widthPx = `${width}px`;

    const cells = document.querySelectorAll(`[data-col-id="${colId}"]`);
    cells.forEach(cell => setElementWidth(cell, widthPx));

    const resizeTargets = document.querySelectorAll(`[data-col-resize-target="${colId}"]`);
    resizeTargets.forEach(target => setElementWidth(target, widthPx));
  }, []);

  const handleResizeColumn = useCallback((e) => {
    if (!colResizeStateRef.current) return;

    e.preventDefault();
    e.stopPropagation();

    const state = colResizeStateRef.current;
    const proposedWidth = state.initWidth + (e.clientX - state.mousePos);
    const lowerBound = Math.min(state.initWidth, minColWidth);
    const clampedWidth = Math.max(lowerBound, proposedWidth);

    finalWidthRef.current = clampedWidth;
    applyWidthToDOM(state.colId, clampedWidth);
  }, [applyWidthToDOM, minColWidth]);

  const initColResizeState = useCallback((e, d) => {
    e.preventDefault();
    e.stopPropagation();

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    const currentWidth = parseInt(colWidthConfigRef.current[d.colId].replace('px', ''));
    const newState = {
      mousePos: e.clientX,
      initWidth: currentWidth,
      colId: d.colId
    };

    finalWidthRef.current = newState.initWidth;
    colResizeStateRef.current = newState;
    setColResizeState(newState);
  }, []);

  const resetColResizeState = useCallback(() => {
    if (!colResizeStateRef.current) return;

    const { colId } = colResizeStateRef.current;
    const finalWidth = finalWidthRef.current;
    const finalWidthPx = `${finalWidth}px`;

    clearBodyDragStyles();

    // Sync final width to React state (single re-render at end of drag)
    if (finalWidth !== null) {
      setColWidthConfig(prev => ({ ...prev, [colId]: finalWidthPx }));
    }

    colResizeStateRef.current = undefined;
    finalWidthRef.current = null;
    setColResizeState(undefined);

    if (builderProps?.updateColWidthsInTableConfig) {
      builderProps.updateColWidthsInTableConfig({
        ...colWidthConfigRef.current,
        [colId]: finalWidthPx
      });
    }
  }, [builderProps, setColWidthConfig]);

  const handleDetectorMouseEnter = (colId) => {
    setHoveredDetector(colId);
  }

  const handleDetectorMouseLeave = () => {
    setHoveredDetector(undefined);
  }

  return ({
    resetColResizeState,
    initColResizeState,
    handleResizeColumn,
    colResizeState,
    hoveredDetector,
    handleDetectorMouseEnter,
    handleDetectorMouseLeave
  })
}